import {useNavigate, useOutletContext, useParams} from 'react-router-dom';
import {useGetFormQuery} from '../../../../api/services/formsService';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FormBuilderComponentTypes} from '../FormBuilder/ComponentList';
import {Box, Button, Link, Stack, styled, Typography} from '@mui/material';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import AdmicityStatus from '../../../../shared-components/AdmicityStatus';
import {getFormRequestStatusInfo} from '../../RequestAndResponses/common/common';
import {
    downloadFormDocument,
    downloadFormResponsePdfReport,
    downloadFormResponsesCsvReport,
    downloadFormResponsesXlsxReport,
    useLazyGenerateFormResponsesReportFormQuery
} from '../../../../api/services/filesService';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import {
    useDeleteFormRequestMutation,
    useGetFormRequestsQuery,
    useGetUsersSubmittedFormRequestQuery,
    useRequestFormResubmissionMutation,
    useResendFormRequestMutation
} from '../../../../api/services/formRequestsService';
import {FORM_REQUEST_STATUSES, FORM_REQUEST_STATUSES_NAMES} from '../../../../constants/formRequestStatuses';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import {formatDate, formatDateTime, isDateExpired} from '../../../../utility/dateUtil';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import DateRangePicker from '../../../../shared-components/Table/V2/components/columnFilters/DateRangePicker';
import NumberRangeFilter from '../../../../shared-components/Table/V2/components/columnFilters/NumberRangeFilter';
import FILTER_OPERATIONS from '../../../../constants/filterOperations';
import useTableState from '../../../../utility/hooks/useTableState';
import useFileDownloader from '../../../../utility/hooks/useFileDownloader';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import {
    useCreateGroupFromFormResponsesMutation,
    useExtendGroupMutation,
    useGetBoarderGroupsQuery, useGetGroupFiltersQuery,
    useGetGroupsByFormQuery
} from '../../../../api/services/groupService';
import useNotification from '../../../../utility/hooks/useNotification';
import CreateGroupFromResponsesDialog from './CreateGroupFromResponsesDialog';
import Tooltip from '@mui/material/Tooltip';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import {MailOutline} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import {useConfirm} from 'material-ui-confirm';
import {useDispatch} from 'react-redux';
import {FORM_STATUSES} from '../../../../constants/formStatuses';
import useUser from '../../../../utility/hooks/useUser';
import {ROLES} from '../../../../constants/roles';
import {
    useGetContactTitlesQuery,
    useGetCountriesQuery,
    useGetRelationsQuery
} from '../../../../api/services/lookupsService';
import GroupDrawer from './GroupDrawer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import {APPROVAL_STATUSES, getApprovalStatusInfo} from '../../../../constants/FormApprovalStatuses';
/* eslint-disable react/prop-types */

const StyledCell = styled('div')({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: '24px',
});

const CustomCell = ({cell}) => {
    const value = cell.getValue();
    return (
        <Tooltip title={value} arrow>
            <StyledCell>
                {value}
            </StyledCell>
        </Tooltip>
    );
};

const CustomHeader = ({label}) => {
    return (
        <Tooltip title={label} arrow>
            <StyledCell>
                {label}
            </StyledCell>
        </Tooltip>
    );
};

const extractDataFromComponentByType = (componentDefinition, lookups) => {
    return ({
            [FormBuilderComponentTypes.SingleChoice]: (id) => [{
                [componentDefinition.id]: componentDefinition.options.find(x => x.id === id)?.description
            }],
            [FormBuilderComponentTypes.OfferStatusChoice]: (id) => [{
                [componentDefinition.id]: componentDefinition.options.find(x => x.id === id)?.description
            }],
            [FormBuilderComponentTypes.IntakeChoice]: (id) => [{
                [componentDefinition.id]: componentDefinition.options.find(x => x.id === id)?.description
            }],
            [FormBuilderComponentTypes.BoarderStatusChoice]: (id) => [{
                [componentDefinition.id]: componentDefinition.options.find(x => x.id === id)?.description
            }],
            [FormBuilderComponentTypes.NumericInputBox]: (id) => (
                (componentDefinition.showEditModeOnly && (componentDefinition.key ?? '').toLowerCase().includes('priority'))
                    ? []
                    : [{[componentDefinition.id]: id}]
            ),
            [FormBuilderComponentTypes.Address]: (address) => {
                const parts = [
                    address?.houseNumber,
                    address?.street,
                    address?.town,
                    address?.postcode,
                    lookups?.countries?.find(c => c.id === address?.countryCode)?.description
                ].filter(Boolean);

                return [{
                    [componentDefinition.id]: parts.join(', ')
                }];
            },
            [FormBuilderComponentTypes.MultiLineInput]: (input) =>
                input
                    ? Object.entries(input)
                        .map(([key, value]) => ({[key]: value}))
                    : [],
            [FormBuilderComponentTypes.QuestionGrid]: (input) => {
                if (!input) return [];
                const isMultiColumn = (componentDefinition.columns && componentDefinition.columns.length > 0);

                if (isMultiColumn) {
                    const nested = Object.entries(input).reduce((acc, [inputId, value]) => {
                        if (value && typeof value === 'object' && !Array.isArray(value)) {
                            acc[inputId] = value;
                        } else {
                            acc[inputId] = value ?? '';
                        }
                        return acc;
                    }, {});

                    return [{ [componentDefinition.id]: nested }];
                }

                return Object.entries(input)
                    .map(([key, value]) => ({ [key]: value }));
            },
            [FormBuilderComponentTypes.Dropdown]: (id) => [{
                [componentDefinition.id]: componentDefinition.options.find(x => x.id === id)?.description
            }],
            [FormBuilderComponentTypes.MultipleChoice]: (ids) =>
                ids
                    ? componentDefinition.options
                        .filter(x => ids.includes(x.id))
                        .map(x => ({[x.id]: true}))
                    : [],
            [FormBuilderComponentTypes.TickBoxGrid]: (values) =>
                values
                    ? Object.entries(values).map(([rowId, selectedColumnIds]) => (
                        {
                            [rowId]: componentDefinition.columns
                                .filter(x => selectedColumnIds.includes(x.id))
                                .map(x => x.title)
                        }
                    ))
                    : [],
            [FormBuilderComponentTypes.MultipleChoiceGrid]: (values) =>
                values
                    ? Object.entries(values).map(([rowId, selectedColumnId]) => ({
                        [rowId]: componentDefinition.columns
                            .find(x => x.id === selectedColumnId)
                            ?.title
                    }))
                    : [],
            [FormBuilderComponentTypes.Signature]: (value) => [{
                [componentDefinition.id]: value ? 'Signed' : ''
            }],
            [FormBuilderComponentTypes.Attachment]: () => [{
                [componentDefinition.id]: componentDefinition.attachments
            }],
            [FormBuilderComponentTypes.UPN]: (value) => [{
                [componentDefinition.id]: value
            }],
            [FormBuilderComponentTypes.PreviousSchoolName]: (value) => [{
                [componentDefinition.id]: value
            }],
            [FormBuilderComponentTypes.PreviousSchoolEmail]: (value) => [{
                [componentDefinition.id]: value
            }],
        })[componentDefinition.type] ??
        function (value) {
            return [{[componentDefinition.id]: value}];
        };
};

const FormResponses = () => {
    const {key} = useOutletContext();
    const {
        pagination,
        filters,
        columnVisibility,
        onFiltersChange,
        onPaginationChange,
        onColumnVisibilityChange
    } = useTableState(key);
    const {id} = useParams();
    const {
        data: form = {},
        isLoading: isLoadingFormData
    } = useGetFormQuery(id);
    const {
        data: responses = {},
        isLoading: isLoadingFormResponses,
        isFetching: isFetchingFormResponses
    } = useGetFormRequestsQuery({
        formId: id,
        pageSize: pagination.pageSize,
        pageNumber: pagination.pageIndex,
        filters: filters.filters,
        responseFilters: filters.responseFilters,
    }, {skip: form.schema === undefined});
    const [createGroup, {
        isSuccess: isCreateGroupSuccess,
        isError: isCreateGroupError,
        error: createGroupError
    }] = useCreateGroupFromFormResponsesMutation();
    const [extendGroup, {
        isSuccess: isExtendGroupSuccess,
        isError: isExtendGroupError,
        error: extendGroupError
    }] = useExtendGroupMutation();
    const [resendFormRequest, {
        isSuccess: isResendFormRequestSuccess,
        isError: isResendFormRequestError
    }] = useResendFormRequestMutation();
    const [requestFormResubmission, {
        isSuccess: isRequestResubmissionSuccess,
        isError: isRequestResubmissionError
    }] = useRequestFormResubmissionMutation();
    const [deleteFormRequest, {
        isError: isDeleteFormRequestError
    }] = useDeleteFormRequestMutation();
    const usersSubmittedFormResult = useGetUsersSubmittedFormRequestQuery(id);
    const navigate = useNavigate();
    const downloadFile = useFileDownloader();
    const [openCreateGroupDialog, setOpenCreateGroupDialog] = useState(false);
    const [openGroupsDrawer, setOpenGroupsDrawer] = useState(false);
    const {showDetailedErrorNotification, showSuccessNotification, showErrorNotification} = useNotification();
    const confirm = useConfirm();
    const dispatch = useDispatch();
    const {user} = useUser();
    const isSchoolAdmin = user?.hasRole?.(ROLES.SCHOOL_ADMIN);
    const lookupTypes = useMemo(() => Array.from(
        new Set(
            form?.schema
                ?.flatMap((f) => f.lookupType || [])
                .filter(Boolean)
        )
    ), [form.schema]);
    const titlesQuery = useGetContactTitlesQuery(undefined, {skip: !lookupTypes.includes('title')});
    const relationsQuery = useGetRelationsQuery(undefined, {skip: !lookupTypes.includes('relationship')});
    const boardStatusesQuery = useGetBoarderGroupsQuery(undefined, {skip: !lookupTypes.includes('boarderStatus')});
    const countriesQuery = useGetCountriesQuery();
    const [generateFormResponsesReportForm] = useLazyGenerateFormResponsesReportFormQuery();
    const allowExportToGroup = form &&
        form.audience !== FORM_AUDIENCE_TYPE.employees &&
        form.audience !== FORM_AUDIENCE_TYPE.externalUsers &&
        form.template?.type !== 'shareable' &&
        !form.isCommunication;
    const {
        data: groupFilters = {modules: []}
    } = useGetGroupFiltersQuery({fetchOnlyCustom: true}, {skip: !allowExportToGroup || !openCreateGroupDialog});
    const {data: groups} = useGetGroupsByFormQuery(id, {skip: !allowExportToGroup || (!openCreateGroupDialog && !openGroupsDrawer)});
    const isLookupsLoading =
        (lookupTypes.includes('title') && titlesQuery.isLoading) ||
        (lookupTypes.includes('relationship') && relationsQuery.isLoading) ||
        (lookupTypes.includes('boarderStatus') && boardStatusesQuery.isLoading);
    const lookups = {
        title: (titlesQuery.data ?? []).map(x => ({
            id: x.id,
            description: x.description,
        })),
        relationship: (relationsQuery.data ?? []).map(x => ({
            id: x.code,
            description: x.description,
        })),
        boarderStatus: (boardStatusesQuery.data ?? []).map(x => ({
            id: x.id,
            description: x.name,
        })),
        countries: (countriesQuery.data ?? []).map(x => ({
            id: x.code,
            description: x.name,
        })),
        gender: [
            {description: 'Male', id: 'male'},
            {description: 'Female', id: 'female'}
        ]
    };

    useEffect(() => {
        if (isCreateGroupSuccess) {
            showSuccessNotification('Groups created');
            handleCloseCreateGroupDialog();
        } else if (isCreateGroupError) {
            showDetailedErrorNotification('Failed to create groups', createGroupError.data.validationErrors);
        }
    }, [isCreateGroupSuccess, isCreateGroupError]);

    useEffect(() => {
        if (isExtendGroupSuccess) {
            handleCloseCreateGroupDialog();
        } else if (isExtendGroupError) {
            showDetailedErrorNotification('Failed to extend groups', extendGroupError.data.validationErrors);
        }
    }, [isExtendGroupSuccess, isExtendGroupError]);

    useEffect(() => {
        if (isResendFormRequestSuccess) {
            showSuccessNotification('Email sent');
        } else if (isResendFormRequestError) {
            showErrorNotification('Failed to resend form request');
        }
    }, [isResendFormRequestSuccess, isResendFormRequestError]);

    useEffect(() => {
        if (isRequestResubmissionSuccess) {
            showSuccessNotification('Resubmission requested');
        } else if (isRequestResubmissionError) {
            showErrorNotification('Failed to request resubmission');
        }
    }, [isRequestResubmissionSuccess, isRequestResubmissionError]);

    useEffect(() => {
        if (isDeleteFormRequestError) {
            showErrorNotification('Failed to delete form request');
        }
    }, [isDeleteFormRequestError]);

    const handleResend = (formId, formRequestId, formRequestAssignmentId) => {
        confirm({
            title: 'Confirmation',
            description: 'Are you sure that you want to resend form request email?',
            confirmationText: 'Confirm'
        })
            .then(() => {
                dispatch(resendFormRequest({
                    formId,
                    formRequestId,
                    formRequestAssignmentId
                }));
            })
            .catch(() => {
            });
    };

    const formSchemaComponentTypes = useMemo(() =>
        (form.schema?.reduce((components, x) => {
            if (x.type !== FormBuilderComponentTypes.AdditionalNotes && x.type !== FormBuilderComponentTypes.Image) {
                components[x.id] = x;
            }
            return components;
        }, {})) ?? {}, [form.schema]);

    const data = useMemo(() =>
        responses?.items
            ? responses.items.map(({data, ...rest}) => ({
                    ...rest,
                    ...Object.assign({},
                        ...Object.entries(formSchemaComponentTypes)
                            .flatMap(([componentId, componentDefinition]) =>
                                data
                                    ? extractDataFromComponentByType(componentDefinition, lookups)(data[componentId]).filter(x => x)
                                    : [])
                    )
                })
            )
            : [], [responses.items, formSchemaComponentTypes]);

    const hasAnyApprovals = form?.hasApproval ?? false;

    const approvalColumns = [
        {
            accessorKey: 'approvalStage',
            header: 'Approval Stage',
            Cell: CustomCell,
            filterFn: FILTER_OPERATIONS.CONTAINS,
            filterVariant: 'text',
        },
        {
            accessorKey: 'approvalStageProgress',
            header: 'Approval Progress',
            Cell: CustomCell,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'approvalStatus',
            header: 'Approval Status',
            /* eslint-disable-next-line react/prop-types */
            Cell: ({ cell: { getValue } }) => (
                <AdmicityStatus {...getApprovalStatusInfo(getValue())} />
            ),
            filterVariant: 'select',
            filterFn: FILTER_OPERATIONS.EQUALS,
            filterSelectOptions: Object.values(APPROVAL_STATUSES).map(s => ({
                value: s,
                label: s,
            })),
        },
        {
            accessorKey: 'approvedBy',
            header: 'Approved By',
            Cell: CustomCell,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'approvedAt',
            header: 'Approved At',
            Cell: ({ cell }) =>
                cell.getValue() ? formatDate(cell.getValue()) : '',
            Filter: DateRangePicker,
            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
        },
        {
            accessorKey: 'approvalNote',
            header: 'Approval Note',
            Cell: CustomCell,
            enableColumnFilter: false,
        },
    ];

    const baseColumns = useMemo(() => (
        {
            privateFormColumns: [
                {
                    accessorKey: 'formName',
                    header: 'Form name',
                    size: 180,
                    enableColumnFilter: false,
                    Cell: CustomCell
                },
                {
                    id: 'person.forename',
                    header: 'First name',
                    accessorFn: row => row.student?.forename ?? row.employee?.forename ?? '',
                },
                {
                    id: 'person.surname',
                    header: 'Last name',
                    accessorFn: row => row.student?.legalSurname ?? row.employee?.legalSurname ?? '',
                },
                ...(!form.allowMultipleSubmissions
                    ? [
                        {
                            accessorKey: 'contacts',
                            header: 'Assignee',
                            minSize: 250,
                            maxSize: 500,
                            enableColumnFilter: false,
                            Cell: ({
                                       cell,
                                       row
                                   }) =>
                                <Stack direction="column" gap={1}>
                                    {
                                        (cell.getValue() ?? []).map((contact, index) => {
                                            const canShowAction =
                                                (row.original.status === FORM_REQUEST_STATUSES.sent ||
                                                    row.original.status === FORM_REQUEST_STATUSES.failedToSend ||
                                                    row.original.status === FORM_REQUEST_STATUSES.created) &&
                                                form.status === FORM_STATUSES.active &&
                                                (!form.expirationDate || !isDateExpired(form.expirationDate));

                                            const canResend =
                                                canShowAction &&
                                                form.enableRecipientNotifications !== false &&
                                                contact.emailNotificationsEnabled !== false;

                                            const showDisabledInfo =
                                                canShowAction &&
                                                form.enableRecipientNotifications !== false &&
                                                contact.emailNotificationsEnabled === false;

                                            return (
                                                <Stack
                                                    key={`assignee_${index}`}
                                                    flexDirection="row"
                                                    alignItems="center"
                                                    onClick={e => e.stopPropagation()}
                                                    sx={{width: '100%'}}
                                                >
                                                    <AccountCircleRoundedIcon
                                                        color="text.secondary"
                                                        fontSize="medium"
                                                        sx={{
                                                            color: 'text.disabled',
                                                            marginRight: 1
                                                        }}
                                                    />
                                                    <Stack direction="column">
                                                        <Typography
                                                            variant="body1">{`${contact.forename} ${contact.surname}`}</Typography>
                                                        <Typography variant="caption">{contact.email}</Typography>
                                                    </Stack>

                                                    {canResend && (
                                                        <IconButton
                                                            onClick={() => handleResend(
                                                                form.id,
                                                                row.original.id,
                                                                contact.formRequestAssignmentId
                                                            )}
                                                            title="Resend Form Request"
                                                        >
                                                            <MailOutline sx={{color: 'primary.main'}}/>
                                                        </IconButton>
                                                    )}

                                                    {showDisabledInfo && (
                                                        <Tooltip
                                                            title="Email notifications are disabled for this contact"
                                                            arrow>
                                                            <InfoOutlinedIcon fontSize="small"/>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            );
                                        })
                                    }
                                </Stack>
                        }

                    ]
                    : []),
                ...(!form.isCommunication
                    ? [
                        {
                            id: 'editedBy',
                            accessorKey: 'editedBy.fullname',
                            header: 'Submitted By',
                            filterFn: FILTER_OPERATIONS.CONTAINS,
                            filterVariant: 'text',
                            size: 180,
                        },
                        {
                            accessorKey: 'editedAt',
                            header: 'Submitted At',
                            Filter: DateRangePicker,
                            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
                            Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
                        },
                        {
                            accessorKey: 'status',
                            header: 'Status',
                            size: 180,
                            filterFn: FILTER_OPERATIONS.EQUALS,
                            filterVariant: 'select',
                            filterSelectOptions: Object.values(FORM_REQUEST_STATUSES).map(statusId => ({
                                value: statusId,
                                label: FORM_REQUEST_STATUSES_NAMES[statusId]
                            })),
                            Cell: ({cell}) => <AdmicityStatus {...getFormRequestStatusInfo(cell.getValue())}/>
                        },
                    ]
                    : []),
                ...approvalColumns,
            ],
            publicFormColumns: [
                {
                    accessorKey: 'formName',
                    header: 'Form name',
                    size: 180,
                    enableColumnFilter: false
                },
                ...(!form.isCommunication
                    ? [
                        {
                            accessorKey: 'editedAt',
                            header: 'Submitted At',
                            Filter: DateRangePicker,
                            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
                            Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
                        },
                        {
                            accessorKey: 'status',
                            header: 'Status',
                            size: 180,
                            filterFn: FILTER_OPERATIONS.EQUALS,
                            filterVariant: 'select',
                            filterSelectOptions: Object.values(FORM_REQUEST_STATUSES).map(statusId => ({
                                value: statusId,
                                label: FORM_REQUEST_STATUSES_NAMES[statusId]
                            })),
                            Cell: ({cell}) => <AdmicityStatus {...getFormRequestStatusInfo(cell.getValue())}/>
                        },
                    ]
                    : []),
                ...approvalColumns,
            ]
        }
    ), [usersSubmittedFormResult, form, hasAnyApprovals]);

    const generateColumnPropsByComponentDefinition = useCallback((componentDefinition) =>
        ({
            [FormBuilderComponentTypes.CheckboxQuestion]: () => [
                {
                    accessorKey: componentDefinition.id,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    Cell: ({cell}) => cell.getValue() ? <CheckBoxRoundedIcon color="primary"/> : '',
                    filterVariant: 'select',
                    filterSelectOptions: [{value: true, label: 'Selected'}]
                }
            ],
            [FormBuilderComponentTypes.LookupDropdown]: () => [
                {
                    accessorKey: componentDefinition.id,
                    id: `${componentDefinition.id}.id`,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    Cell: ({cell}) => cell.getValue()?.description,
                    filterVariant: 'select',
                    filterSelectOptions: lookups[componentDefinition.lookupType]?.map(x => ({
                        value: x.id,
                        label: x.description
                    })),
                    filterFn: FILTER_OPERATIONS.EQUALS,
                }
            ],
            [FormBuilderComponentTypes.Dropdown]: () => [
                {
                    accessorKey: componentDefinition.id,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    Cell: CustomCell,
                    filterVariant: 'select',
                    filterSelectOptions: componentDefinition.options.map(x => ({value: x.id, label: x.description}))
                }
            ],
            [FormBuilderComponentTypes.SingleChoice]: () => [
                {
                    accessorKey: componentDefinition.id,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    Cell: CustomCell,
                    filterVariant: 'select',
                    filterSelectOptions: componentDefinition.options.map(x => ({value: x.id, label: x.description}))
                }
            ],
            [FormBuilderComponentTypes.OfferStatusChoice]: () => [
                {
                    accessorKey: componentDefinition.id,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    Cell: CustomCell,
                    filterVariant: 'select',
                    filterSelectOptions: componentDefinition.options.map(x => ({value: x.id, label: x.description}))
                }
            ],
            [FormBuilderComponentTypes.IntakeChoice]: () => [
                {
                    accessorKey: componentDefinition.id,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    Cell: CustomCell,
                    filterVariant: 'select',
                    filterSelectOptions: componentDefinition.options.map(x => ({value: x.id, label: x.description}))
                }
            ],
            [FormBuilderComponentTypes.BoarderStatusChoice]: () => [
                {
                    accessorKey: componentDefinition.id,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    Cell: CustomCell,
                    filterVariant: 'select',
                    filterSelectOptions: componentDefinition.options.map(x => ({value: x.id, label: x.description}))
                }
            ],
            [FormBuilderComponentTypes.Address]: () => [
                {
                    accessorKey: componentDefinition.id,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    Cell: CustomCell,
                    enableColumnFilter: false
                }
            ],
            [FormBuilderComponentTypes.MultipleChoice]: () => componentDefinition.options
                .map(x => ({
                    accessorKey: x.id,
                    header: `${componentDefinition.description} (${x.description})`,
                    Header: (<CustomHeader label={`${componentDefinition.description} (${x.description})`}/>),
                    Cell: ({cell}) => cell.getValue() ? <CheckBoxRoundedIcon color="primary"/> : '',
                    filterVariant: 'select',
                    filterSelectOptions: [{value: x.id, label: 'Selected'}]
                })),
            [FormBuilderComponentTypes.TickBoxGrid]: () => componentDefinition.rows
                .map(x => ({
                    accessorKey: x.id,
                    header: `${componentDefinition.description} (${x.title})`,
                    Header: (<CustomHeader label={`${componentDefinition.description} (${x.title})`}/>),
                    Cell: ({cell}) => {
                        const value = cell.getValue();
                        const displayValue = value?.length === 1 && value[0] === '' ? 'Selected' : value?.join('; ') || '';
                        return (
                            <Tooltip title={displayValue} arrow>
                                <StyledCell>
                                    {displayValue}
                                </StyledCell>
                            </Tooltip>
                        );
                    },
                    filterVariant: 'multi-select',
                    filterSelectOptions: componentDefinition.columns.map(c => ({value: c.id, label: c.title})),
                    filterFn: FILTER_OPERATIONS.MULTISELECT
                })),
            [FormBuilderComponentTypes.MultipleChoiceGrid]: () => componentDefinition.rows
                .map(x => ({
                    accessorKey: x.id,
                    header: `${componentDefinition.description} (${x.title})`,
                    Header: (<CustomHeader label={`${componentDefinition.description} (${x.title})`}/>),
                    Cell: ({cell}) => {
                        const value = cell.getValue();
                        return (
                            <Tooltip title={value} arrow>
                                <StyledCell>
                                    {value === '' ? 'Selected' : value ? value : ''}
                                </StyledCell>
                            </Tooltip>
                        );
                    },
                    filterVariant: 'select',
                    filterSelectOptions: componentDefinition.columns.map(c => ({value: c.id, label: c.title}))
                })),
            [FormBuilderComponentTypes.LinearScale]: () => [{
                accessorKey: componentDefinition.id,
                header: componentDefinition.description,
                Header: (<CustomHeader label={componentDefinition.description}/>),
                filterVariant: 'range-slider',
                filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
                muiFilterSliderProps: {
                    marks: true,
                    min: componentDefinition.startFrom,
                    max: componentDefinition.endAt,
                    step: 1
                }
            }],
            [FormBuilderComponentTypes.Attachment]: () => [{
                accessorKey: componentDefinition.id,
                header: componentDefinition.label,
                Header: (<CustomHeader label={componentDefinition.label}/>),
                enableColumnFilter: false
            }],
            [FormBuilderComponentTypes.Signature]: () => [{
                accessorKey: componentDefinition.id,
                header: componentDefinition.label,
                Header: (<CustomHeader label={componentDefinition.label}/>),
                enableColumnFilter: false
            }],
            [FormBuilderComponentTypes.Attachment]: () => [
                {
                    accessorKey: componentDefinition.id,
                    header: componentDefinition.label,
                    Header: (<CustomHeader label={componentDefinition.label}/>),
                    enableColumnFilter: false,
                    Cell: ({cell}) => <Stack direction="column">
                        {
                            cell.getValue()?.map(x => (
                                <Link
                                    key={x.name}
                                    component="button"
                                    variant="body2"
                                    onClick={async () => await downloadFile(downloadFormDocument, {documentId: x.id})}
                                >
                                    {x.name}
                                </Link>
                            ))
                        }
                    </Stack>
                }
            ],
            [FormBuilderComponentTypes.Date]: () => [
                {
                    accessorKey: componentDefinition.id,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    Filter: DateRangePicker,
                    filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE_DATE,
                    Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
                }
            ],
            [FormBuilderComponentTypes.DateTime]: () => [
                {
                    accessorKey: componentDefinition.id,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    Filter: DateRangePicker,
                    filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE_DATE,
                    Cell: ({cell}) => cell.getValue() ? formatDateTime(cell.getValue()) : '',
                }
            ],
            [FormBuilderComponentTypes.MultiLineInput]: () =>
                componentDefinition.inputs
                    .map(x => ({
                        accessorKey: x.id,
                        header: x.title,
                        Header: (<CustomHeader label={x.title}/>)
                    })),
            [FormBuilderComponentTypes.QuestionGrid]: () => {
                const isMultiColumn = componentDefinition.columns && componentDefinition.columns.length > 0;

                if (isMultiColumn) {
                    return componentDefinition.inputs.flatMap(input =>
                        componentDefinition.columns.map(column => ({
                            accessorKey: `${componentDefinition.id}.${input.id}.${column.id}`,
                            header: `${input.title} - ${column.title || 'Untitled Column'}`,
                            Cell: CustomCell,
                            Header: (<CustomHeader label={`${input.title} - ${column.title || 'Untitled Column'}`}/>)
                        }))
                    );
                } else {
                    return componentDefinition.inputs.map(x => ({
                        accessorKey: x.id,
                        header: x.title,
                        Cell: CustomCell,
                        Header: (<CustomHeader label={x.title}/>)
                    }));
                }
            },
            [FormBuilderComponentTypes.TextBox]: () => [
                {
                    accessorKey: componentDefinition.id,
                    Cell: CustomCell,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    filterFn: FILTER_OPERATIONS.CONTAINS
                }
            ],
            [FormBuilderComponentTypes.NumericInputBox]: () => (
                (componentDefinition?.key || '').toLowerCase().includes('priority')
                    ? []
                    : [{
                        accessorKey: componentDefinition.id,
                        header: componentDefinition.description || componentDefinition.label,
                        Header: (<CustomHeader label={componentDefinition.description || componentDefinition.label}/>),
                        Cell: CustomCell,
                        Filter: NumberRangeFilter,
                        filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE_NUMERIC,
                    }]
            ),
            [FormBuilderComponentTypes.SingleText]: () => [
                {
                    accessorKey: componentDefinition.id,
                    Cell: CustomCell,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    filterFn: FILTER_OPERATIONS.CONTAINS
                }
            ],
            [FormBuilderComponentTypes.UPN]: () => [
                {
                    accessorKey: componentDefinition.id,
                    Cell: CustomCell,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    filterFn: FILTER_OPERATIONS.CONTAINS
                }
            ],
            [FormBuilderComponentTypes.PreviousSchoolName]: () => [
                {
                    accessorKey: componentDefinition.id,
                    Cell: CustomCell,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    filterFn: FILTER_OPERATIONS.CONTAINS
                }
            ],
            [FormBuilderComponentTypes.PreviousSchoolEmail]: () => [
                {
                    accessorKey: componentDefinition.id,
                    Cell: CustomCell,
                    header: componentDefinition.description,
                    Header: (<CustomHeader label={componentDefinition.description}/>),
                    filterFn: FILTER_OPERATIONS.CONTAINS
                }
            ],
        })[componentDefinition.type] ??
        function () {
            return [];
        }, [isLookupsLoading]);

    const resolveComponentPathInfo = id => {
        if (id.includes('.')) {
            const compositeKey = id.split('.');

            return {
                componentId: compositeKey[compositeKey.length - 1],
                jsonParentPaths: compositeKey.slice(0, -1)
            };
        }

        return {
            componentId: id,
            jsonParentPaths: dynamicColumns.paths[id] ?? []
        };
    };

    const handleColumnFiltersApply = (filters, quickFilters, filtersFns) => {
        const columnFilters = filters.reduce((acc, x) => {
                const baseColumn = baseColumns.publicFormColumns.find(c => c.accessorKey === x.id) ||
                    baseColumns.privateFormColumns.find(c => c.accessorKey === x.id || c.id === x.id);

                if (baseColumn) {
                    acc.filters.push({
                        propertyName: x.id === 'student.legalSurname'
                            ? 'student.surname'
                            : x.id === 'student.forename' ? 'student.CalculatedPreferredForename' : x.id,
                        value: x.id === 'editedBy' ? x.value.split(',') : x.value,
                        operation: filtersFns[baseColumn.accessorKey] || filtersFns[baseColumn.id]
                    });
                } else {
                    const {componentId, jsonParentPaths} = resolveComponentPathInfo(x.id);

                    acc.responseFilters.push({
                        propertyName: componentId,
                        value: x.value,
                        jsonParentPaths,
                        operation: filtersFns[x.id],
                    });
                }

                return acc;
            }, {filters: [], responseFilters: []}
        );

        onFiltersChange({filters: columnFilters});
    };

    const handleColumnVisibilityChange = value => {
        const result = Object.entries(value).reduce((acc, [accessorKey, hide]) => {
                if (baseColumns.publicFormColumns.some(c => c.accessorKey === accessorKey) ||
                    baseColumns.privateFormColumns.some(c => c.accessorKey === accessorKey)) {
                    acc.default[accessorKey] = hide;
                } else {
                    acc.dynamic[id][accessorKey] = hide;
                }

                return acc;
            },
            {
                default: {},
                dynamic: {
                    [id]: {}
                }
            }
        );

        onColumnVisibilityChange({...columnVisibility, ...result});
    };

    const dynamicColumns = useMemo(() => {
        const dynamicColumns = Object.values(formSchemaComponentTypes).reduce((acc, x) => {
            const columns = generateColumnPropsByComponentDefinition(x)();

            acc.columns.push(...columns);
            columns.forEach(c => {
                if (c.accessorKey !== x.id) {
                    acc.paths[c.accessorKey] = [x.id];
                }
            });

            return acc;
        }, {columns: [], paths: {}});

        return {
            columns: dynamicColumns.columns.length
                ? [
                    ...(
                        {
                            [FORM_ACCESS_LEVELS.private]: baseColumns.privateFormColumns,
                            [FORM_ACCESS_LEVELS.public]: baseColumns.publicFormColumns,
                            [FORM_ACCESS_LEVELS.publicWithStudentLink]: baseColumns.privateFormColumns,
                        }[form.accessLevel] ?? []
                    ),
                    ...dynamicColumns.columns
                ]
                : [
                    ...(
                        {
                            [FORM_ACCESS_LEVELS.private]: baseColumns.privateFormColumns,
                            [FORM_ACCESS_LEVELS.public]: baseColumns.publicFormColumns,
                            [FORM_ACCESS_LEVELS.publicWithStudentLink]: baseColumns.privateFormColumns,
                        }[form.accessLevel] ?? []
                    )
                ],
            paths: dynamicColumns.paths
        };
    }, [form.accessLevel, formSchemaComponentTypes, usersSubmittedFormResult, isLookupsLoading, hasAnyApprovals]);

    const downloadCsvReport = async () =>
        await downloadFile(
            downloadFormResponsesCsvReport,
            {
                formId: id,
                filters: filters.filters,
                responseFilters: filters.responseFilters,
            });

    const downloadPdfReport = async (formRequestId, isBulk = true) => {
        if (!isBulk && formRequestId) {
            await downloadFile(
                downloadFormResponsePdfReport,
                {
                    formId: id,
                    filters: filters.filters,
                    responseFilters: filters.responseFilters,
                    formRequestId
                });
        } else {
            await generateFormResponsesReportForm({
                formId: id,
                filters: filters.filters,
                responseFilters: filters.responseFilters,
                formRequestId: null
            });
            showSuccessNotification('The PDFs is being created. It will be sent to your email address when the process is complete.');
        }
    };

    const downloadXlsxReport = async () =>
        await downloadFile(
            downloadFormResponsesXlsxReport,
            {
                formId: id,
                filters: filters.filters,
                responseFilters: filters.responseFilters,
            });

    const handleCreateGroup = async data => {
        const {groups, parentGroupFilterId, isGroupDynamic} = data;
        const groupsPayload = groups.map(({groupName, formCategoryId, questionAnswers}) => {
            const responseFilters = (questionAnswers || []).map(({relatedComponentId, targetStateId}) => {
                const {componentId, jsonParentPaths} = resolveComponentPathInfo(relatedComponentId);
                return {
                    propertyName: componentId,
                    value: targetStateId,
                    jsonParentPaths,
                    operation: FILTER_OPERATIONS.EQUALS,
                };
            });
            return {
                groupName,
                formCategoryId,
                responseFilters
            };
        });

        await createGroup({
            formId: id,
            parentGroupFilterId: parentGroupFilterId && parentGroupFilterId !== '' ? parentGroupFilterId : null,
            isDynamic: isGroupDynamic,
            groups: groupsPayload,
        });
    };

    const handleExtendGroup = async data => {
        const {operations} = data;
        const operationsPayload = operations.map(({existingGroupFilterId, questionAnswers}) => {
            const responseFilters = (questionAnswers || []).map(({relatedComponentId, targetStateId}) => {
                const {componentId, jsonParentPaths} = resolveComponentPathInfo(relatedComponentId);
                return {
                    propertyName: componentId,
                    value: targetStateId,
                    jsonParentPaths,
                    operation: FILTER_OPERATIONS.EQUALS,
                };
            });
            return {
                groupFilterId: existingGroupFilterId,
                responseFilters
            };
        });

        await extendGroup({
            formId: id,
            operations: operationsPayload
        });
    };

    const handleDeleteFormRequest = async formRequest =>
        confirm({
            title: 'Confirm Deletion',
            description: 'Are you sure you want to delete this form request?',
            confirmationText: 'Delete'
        })
            .then(async () => {
                await deleteFormRequest({formId: form.id, requestId: formRequest.id});
            })
            .catch(_ => {
            });

    const handleRequestResubmission = async (formRequest) =>
        confirm({
            title: 'Confirmation',
            description: 'The recipient will be notified to resubmit the form. Their current responses are retained and can be edited before they resubmit.',
            confirmationText: 'Confirm'
        })
            .then(async () => {
                await requestFormResubmission({
                    formId: form.id,
                    formRequestId: formRequest.id,
                });
            })
            .catch(_ => {
            });

    const getRowActions = (row) => {
        const actions = [
            {
                label: 'View form request',
                action: params => navigate(`${params.id}`)
            }
        ];

        if (row.status === FORM_REQUEST_STATUSES.submitted) {
            actions.push({
                label: 'Download PDF',
                action: params => downloadPdfReport(params.id, false)
            });

            if (isSchoolAdmin && form.accessLevel !== FORM_ACCESS_LEVELS.public && !form.allowMultipleSubmissions && form.status !== FORM_STATUSES.archived && form.status !== FORM_STATUSES.expired && (!form.expirationDate || !isDateExpired(form.expirationDate))) {
                actions.push({
                    label: 'Request resubmission',
                    action: handleRequestResubmission
                });
            }
        }

        actions.push({
            label: 'Delete',
            action: handleDeleteFormRequest
        });

        return actions;
    };

    const handleCloseCreateGroupDialog = () => setOpenCreateGroupDialog(false);

    return (
        <>
            <Box sx={{marginBottom: 2}}>
                <Button
                    variant="text"
                    startIcon={<ArrowBackRoundedIcon/>}
                    onClick={() => navigate('/forms')}
                >
                    Back to forms
                </Button>
            </Box>
            <AdmicityTableV2
                columns={dynamicColumns.columns}
                data={data}
                initialState={{
                    columnFilters: Object.values(filters)
                        .filter(x => x)
                        .flatMap(x => x.map(f => ({id: f.propertyName, value: f.value})))
                }}
                isLoading={isLoadingFormResponses || isLoadingFormData}
                isRefetching={isFetchingFormResponses}
                totalItemCount={responses.totalCount}
                pagination={pagination}
                columnVisibility={{
                    ...columnVisibility.default,
                    ...(columnVisibility.dynamic?.[id]?.values ?? {}),
                    approvalStage: hasAnyApprovals,
                    approvalStageProgress: hasAnyApprovals,
                    approvalStatus: hasAnyApprovals,
                    approvedBy: hasAnyApprovals,
                    approvedAt: hasAnyApprovals,
                    approvalNote: hasAnyApprovals
                }}
                onPaginationModelChange={onPaginationChange}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                onColumnFiltersApply={handleColumnFiltersApply}
                rowActions={row => getRowActions(row)}
                toolbarActions={
                    allowExportToGroup
                        ? [
                            {
                                type: 'button',
                                label: 'Export to Group',
                                startIcon: <GroupAddRoundedIcon/>,
                                onClick: () => setOpenCreateGroupDialog(true)
                            },
                            {
                                type: 'button',
                                label: 'Show Linked Groups',
                                startIcon: <InfoOutlinedIcon/>,
                                onClick: () => setOpenGroupsDrawer(true)
                            }
                        ]
                        : []
                }
                toolbarIconActions={
                    [{
                        icon: <FileDownloadRoundedIcon/>,
                        title: 'Download report',
                        type: 'select',
                        hideIfEmpty: true,
                        menuItems: [
                            {
                                label: 'Download CSV',
                                onClick: downloadCsvReport
                            },
                            {
                                label: 'Download XLSX',
                                onClick: downloadXlsxReport
                            },
                            {
                                label: 'Download PDF',
                                onClick: downloadPdfReport
                            },
                        ]
                    }]
                }
                tableProps={{
                    enableSorting: false,
                    defaultColumn: {
                        size: 300,
                        maxSize: 1500,
                        minSize: 180,
                    }
                }}
            />
            <CreateGroupFromResponsesDialog
                onClose={handleCloseCreateGroupDialog}
                onCreateGroup={handleCreateGroup}
                onExtendGroup={handleExtendGroup}
                title={<Stack>
                    <Typography variant="h6">
                        Export to Group
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">Create a new group or add members to an
                        existing group based on
                        their form responses.</Typography>
                </Stack>}
                groupFilters={groupFilters}
                open={openCreateGroupDialog}
                schema={form.schema}
                lookups={lookups}
                groups={groups}
                membersType={form.audience
                    ? form.audience === FORM_AUDIENCE_TYPE.employees
                        ? GROUP_MEMBERS_TYPE.employee
                        : GROUP_MEMBERS_TYPE.students
                    : form.accessLevel === FORM_ACCESS_LEVELS.public
                        ? GROUP_MEMBERS_TYPE.students
                        : GROUP_MEMBERS_TYPE.none
                }
            />
            <GroupDrawer
                open={openGroupsDrawer}
                onClose={() => setOpenGroupsDrawer(false)}
                formName={form?.name}
                groups={groups}
            />
        </>
    );
};

export default FormResponses;